import { alpha, Box, Button, Dialog, DialogActions, DialogContent, FormControl, FormControlLabel, FormLabel, Grid, MenuItem, Radio, RadioGroup, Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { addYears, startOfToday, format, parseISO } from "date-fns";
import { useFormik } from "formik";
import { Colors } from "../../../../styles/theme";
import { SelectPractioner } from "../../../common";
import AppDialogTitle from "../../../ui/appDialogTitle";
import SimpleDatePicker from "../../../ui/simpleDatePicker";
import { RxService } from "../../../../service/rxService";
import { toast } from "react-toastify";
import CustomerInfobar from "../../customerInfobar";
import { 
    axisValues, 
    addValues, 
    rxModel,
    parseForDatabase 
} from "../../../../utils/rxValues";
import { 
    SPHERE_MIN, 
    SPHERE_MAX, 
    SPHERE_INCREMENT, 
    CYLINDER_MIN,
    CYLINDER_MAX,
    CYLINDER_INCREMENT
} from "../../../../utils/prescriptionConstants";
import SignedValueSelector from "./SignedValueSelector";

export default function AddRxDialog({ onClose, open, customer }) {
    
    const formik = useFormik({
        initialValues: { 
            ...rxModel, 
            customerId: customer.customer.id,
            rxDate: format(startOfToday(), 'yyyy-MM-dd'),
            expiryDate: format(addYears(startOfToday(), 1), 'yyyy-MM-dd')
        },
        onSubmit: async (data) => {
            if (!data.practitionerId) {
                toast.error('Please select a practitioner');
                return;
            }
            try {
                // Convert display format to database format
                const cleanData = {
                    ...data,
                    odSph: parseForDatabase.sphere(data.odSph),
                    odCyl: parseForDatabase.cylinder(data.odCyl),
                    odAxis: parseForDatabase.axis(data.odAxis),
                    odAdd: parseForDatabase.add(data.odAdd),
                    osSph: parseForDatabase.sphere(data.osSph),
                    osCyl: parseForDatabase.cylinder(data.osCyl),
                    osAxis: parseForDatabase.axis(data.osAxis),
                    osAdd: parseForDatabase.add(data.osAdd),
                    odDistPd: data.odDistPd || null,
                    odNearPd: data.odNearPd || null,
                    osDistPd: data.osDistPd || null,
                    osNearPd: data.osNearPd || null,
                    odPrism: data.odPrism || null,
                    osPrism: data.osPrism || null
                };
                const result = await RxService.save(cleanData);
                const { code, message } = result;
                if (code !== 0) {
                    toast.error(message || 'Failed to save rx. Please try again.');
                    return;
                }
                toast.success('Rx saved successfully!');
                onClose();
            } catch (error) {
                toast.error('Failed to save rx. Please try again.');
                console.error('Error saving rx:', error);
            }
        }
    });

    const handleSignedValueChange = (fieldName, fieldValue) => {
        formik.setFieldValue(fieldName, fieldValue);
        formik.setFieldTouched(fieldName, true, false);
    };

    function setExpiryDate(years) {
        const rxDate = formik.values.rxDate || format(startOfToday(), 'yyyy-MM-dd');
        const parsedDate = parseISO(rxDate);
        const newExpiryDate = addYears(parsedDate, years);
        formik.setFieldValue('expiryDate', format(newExpiryDate, 'yyyy-MM-dd'), false);
    }

    return (
        <Dialog onClose={onClose} open={open} fullWidth maxWidth={"lg"}>
            <AppDialogTitle onClose={onClose} title="New Rx" />
            <form onSubmit={formik.handleSubmit}>
                <DialogContent>
                <Box display="flex" alignItems="baseline" sx={{ p: 1, borderBottom: '1px solid #ccc', backgroundColor: '#f9f9f9', pb: 1 }}>
                    <CustomerInfobar customerData={customer} />
                </Box>                    
                    <Grid container columnGap={1} sx={{ mb: 1 }}>
                        <Grid item sx={{ background: '#f6f6f6', p: 1 }}>
                            <Stack>
                                <SelectPractioner
                                    sx={{ mb: 2 }}
                                    setPractitioner={(practitioner) => formik.setFieldValue('practitionerId', practitioner)}
                                    practitioner={formik.values.practitionerId} />

                                <SimpleDatePicker
                                    label={"Rx Date"}
                                    onChange={(value) => formik.setFieldValue('rxDate', value)}
                                    value={formik.values.rxDate}
                                />
                                <SimpleDatePicker
                                    label={"Expiry Date"}
                                    onChange={(value) => formik.setFieldValue('expiryDate', value)}
                                    value={formik.values.expiryDate}
                                />
                                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                    <Button 
                                        size="small" 
                                        variant="outlined"
                                        onClick={() => setExpiryDate(1)}
                                    >
                                        +1 Year
                                    </Button>
                                    <Button 
                                        size="small" 
                                        variant="outlined"
                                        onClick={() => setExpiryDate(2) }
                                    >
                                        +2 Years
                                    </Button>
                                </Stack>
                            </Stack>
                        </Grid>
                        
                        <Grid item sx={{ background: '#f6f6f6', p: 1, minWidth: '200px' }}>
                            <FormControl>
                                <FormLabel>Rx Type</FormLabel>
                                <RadioGroup
                                    row
                                    name="rxType"
                                    onChange={formik.handleChange}
                                    value={formik.values.rxType}
                                >
                                    <FormControlLabel value="glasses" control={<Radio />} label="Glasses" />
                                    <FormControlLabel value="contacts" control={<Radio />} label="Contacts" />
                                </RadioGroup>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <TableContainer>
                        <Table size="small">
                            <TableHead sx={{ background: alpha(Colors.primary, 0.5) }}>
                                <TableRow>
                                    <TableCell></TableCell>
                                    <TableCell>Sphere</TableCell>
                                    <TableCell>Cylinder</TableCell>
                                    <TableCell>Axis</TableCell>
                                    <TableCell>Add</TableCell>
                                    {formik.values.rxType === 'glasses' && (
                                        <>
                                            <TableCell>Prism</TableCell>
                                            <TableCell>Base</TableCell>
                                            <TableCell>D PD</TableCell>
                                            <TableCell>N PD</TableCell>
                                        </>
                                    )}
                                    {(formik.values.rxType === 'contacts') && (
                                        <>
                                            <TableCell>BC</TableCell>
                                            <TableCell>Diameter</TableCell>
                                            <TableCell>K1</TableCell>
                                            <TableCell>K2</TableCell>
                                        </>
                                    )}
                                    <TableCell>VA</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell>OD</TableCell>
                                    <TableCell>
                                        <SignedValueSelector
                                            name="odSph"
                                            value={formik.values.odSph}
                                            min={SPHERE_MIN}
                                            max={SPHERE_MAX}
                                            step={SPHERE_INCREMENT}
                                            onChange={handleSignedValueChange}
                                            textFieldSx={{ minWidth: 80 }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <SignedValueSelector
                                            name="odCyl"
                                            value={formik.values.odCyl}
                                            min={CYLINDER_MIN}
                                            max={CYLINDER_MAX}
                                            step={CYLINDER_INCREMENT}
                                            onChange={handleSignedValueChange}
                                            textFieldSx={{ minWidth: 80 }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Select
                                            name="odAxis"
                                            onChange={formik.handleChange}
                                            value={formik.values.odAxis}
                                            size="small"
                                            displayEmpty
                                            sx={{ minWidth: 80 }}>
                                            <MenuItem value="">-</MenuItem>
                                            {axisValues.map(val => <MenuItem key={val} value={val}>{val}</MenuItem>)}
                                        </Select>
                                    </TableCell>
                                    <TableCell>
                                        <Select
                                            name="odAdd"
                                            onChange={formik.handleChange}
                                            value={formik.values.odAdd}
                                            size="small"
                                            displayEmpty
                                            sx={{ minWidth: 80 }}>
                                            <MenuItem value="">-</MenuItem>
                                            {addValues.map(val => <MenuItem key={val} value={val}>+{val}</MenuItem>)}
                                        </Select>
                                    </TableCell>
                                    {formik.values.rxType === 'glasses' && (
                                        <>
                                            <TableCell>
                                                <TextField
                                                    name="odPrism"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.odPrism}
                                                    size="small" />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    name="odPrismBase"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.odPrismBase}
                                                    size="small" />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    name="odDistPd"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.odDistPd}
                                                    size="small" />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    name="odNearPd"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.odNearPd}
                                                    size="small" />
                                            </TableCell>
                                        </>
                                    )}
                                    {(formik.values.rxType === 'contacts') && (
                                        <>
                                            <TableCell>
                                                <TextField
                                                    name="odBc"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.odBc}
                                                    size="small" />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    name="odContactLensDiameter"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.odContactLensDiameter}
                                                    size="small" />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    name="odK1"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.odK1}
                                                    size="small" />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    name="odK2"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.odK2}
                                                    size="small" />
                                            </TableCell>
                                        </>
                                    )}
                                    <TableCell>
                                        <TextField
                                            name="odVa"
                                            onChange={formik.handleChange}
                                            value={formik.values.odVa}
                                            size="small" />
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            OS
                                            <Button 
                                                size="small" 
                                                variant="text" 
                                                onClick={() => {
                                                    formik.setFieldValue('osSph', formik.values.odSph);
                                                    formik.setFieldValue('osCyl', formik.values.odCyl);
                                                    formik.setFieldValue('osAxis', formik.values.odAxis);
                                                    formik.setFieldValue('osAdd', formik.values.odAdd);
                                                    formik.setFieldValue('osPrism', formik.values.odPrism);
                                                    formik.setFieldValue('osPrismBase', formik.values.odPrismBase);
                                                    formik.setFieldValue('osDistPd', formik.values.odDistPd);
                                                    formik.setFieldValue('osNearPd', formik.values.odNearPd);
                                                    formik.setFieldValue('osVa', formik.values.odVa);
                                                    if (formik.values.rxType !== 'glasses') {
                                                        formik.setFieldValue('osBc', formik.values.odBc);
                                                        formik.setFieldValue('osContactLensDiameter', formik.values.odContactLensDiameter);
                                                        formik.setFieldValue('osK1', formik.values.odK1);
                                                        formik.setFieldValue('osK2', formik.values.odK2);
                                                    }
                                                }}
                                                sx={{ fontSize: '0.7rem', minWidth: 'auto', p: 0.5 }}
                                            >
                                                Copy OD
                                            </Button>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <SignedValueSelector
                                            name="osSph"
                                            value={formik.values.osSph}
                                            min={SPHERE_MIN}
                                            max={SPHERE_MAX}
                                            step={SPHERE_INCREMENT}
                                            onChange={handleSignedValueChange}
                                            textFieldSx={{ minWidth: 80 }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <SignedValueSelector
                                            name="osCyl"
                                            value={formik.values.osCyl}
                                            min={CYLINDER_MIN}
                                            max={CYLINDER_MAX}
                                            step={CYLINDER_INCREMENT}
                                            onChange={handleSignedValueChange}
                                            textFieldSx={{ minWidth: 80 }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Select
                                            name="osAxis"
                                            onChange={formik.handleChange}
                                            value={formik.values.osAxis}
                                            size="small"
                                            displayEmpty
                                            sx={{ minWidth: 80 }}>
                                            <MenuItem value="">-</MenuItem>
                                            {axisValues.map(val => <MenuItem key={val} value={val}>{val}</MenuItem>)}
                                        </Select>
                                    </TableCell>
                                    <TableCell>
                                        <Select
                                            name="osAdd"
                                            onChange={formik.handleChange}
                                            value={formik.values.osAdd}
                                            size="small"
                                            displayEmpty
                                            sx={{ minWidth: 80 }}>
                                            <MenuItem value="">-</MenuItem>
                                            {addValues.map(val => <MenuItem key={val} value={val}>+{val}</MenuItem>)}
                                        </Select>
                                    </TableCell>
                                    {formik.values.rxType === 'glasses' && (
                                        <>
                                            <TableCell>
                                                <TextField
                                                    name="osPrism"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.osPrism}
                                                    size="small" />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    name="osPrismBase"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.osPrismBase}
                                                    size="small" />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    name="osDistPd"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.osDistPd}
                                                    size="small" />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    name="osNearPd"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.osNearPd}
                                                    size="small" />
                                            </TableCell>
                                        </>
                                    )}
                                    {(formik.values.rxType === 'contacts') && (
                                        <>
                                            <TableCell>
                                                <TextField
                                                    name="osBc"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.osBc}
                                                    size="small" />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    name="osContactLensDiameter"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.osContactLensDiameter}
                                                    size="small" />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    name="osK1"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.osK1}
                                                    size="small" />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    name="osK2"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.osK2}
                                                    size="small" />
                                            </TableCell>
                                        </>
                                    )}
                                    <TableCell>
                                        <TextField
                                            name="osVa"
                                            onChange={formik.handleChange}
                                            value={formik.values.osVa}
                                            size="small" />
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TextField
                        sx={{ mb: 1, mt: 2 }}
                        name="notes"
                        onChange={formik.handleChange}
                        value={formik.values.notes}
                        multiline 
                        rows={4} 
                        label="Notes" 
                        variant="standard" 
                        fullWidth />

                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={() => formik.resetForm()}>Reset All</Button>
                    <Button variant="outlined" onClick={onClose}>Cancel</Button>
                    <Button color="primary" variant="contained" type="submit" autoFocus>
                        Save
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}